import Vue from 'vue'
import $api from '@/shared/services/api'
import loading from '@/shared/plugins/loading'
import { esperaDoBoot } from '@/shared/common/functions/pdvOffline'

const TTL_DOMINIO = 5 * 60 * 1000

const chaveCacheDominio = () => 'dominio_dados_' + window.location.host

// ignoraValidade: sem internet, o cache vencido é melhor que a tela em branco (PDV offline).
function leCacheDominio(ignoraValidade = false){
    try {
        const bruto = localStorage.getItem(chaveCacheDominio())
        if(!bruto){
            return null
        }
        const cache = JSON.parse(bruto)
        if(!cache || !cache.ts){
            return null
        }
        const idade = Date.now() - cache.ts
        // Idade negativa = relógio do usuário voltou: sem isso o cache não venceria nunca
        if(!ignoraValidade && (idade < 0 || idade > TTL_DOMINIO)){
            return null
        }
        return cache.data
    } catch(e){
        return null
    }
}

function gravaCacheDominio(data){
    try {
        localStorage.setItem(chaveCacheDominio(), JSON.stringify({ data, ts: Date.now() }))
    } catch(e){
        return
    }
}

// Resposta que nunca chegou ao servidor. O $api devolve o XMLHttpRequest nesse caso (status 0);
// qualquer status HTTP de verdade (400, 500, 502...) é erro do servidor e segue o caminho de sempre.
function semRespostaHttp(retorno){
    if(!retorno){
        return true
    }
    if(typeof retorno.status == 'string'){
        return false
    }
    return !(Number(retorno.status) >= 100)
}

// O app responde em /v1/dominio com o mesmo corpo que o ERP responderia.
async function leDominioDoServidorDaLoja(){
    try {
        const resposta = await fetch(window.location.origin + '/v1/dominio', { cache: 'no-store' })
        if(!resposta.ok){
            return null
        }
        const corpo = await resposta.json()
        return (corpo && corpo.data && corpo.data.Dominio) || null
    } catch(erro){
        return null
    }
}

// Campos que o backend atualiza sozinho (ex.: ultimo_acesso a cada login):
// não contam como mudança do domínio, senão a página recarregaria à toa
const CAMPOS_VOLATEIS = ['ultimo_acesso', 'modificado_em', 'dominio_ultima_atualizacao']

function assinaturaDominio(dominio){
    const copia = Object.assign({}, dominio)
    CAMPOS_VOLATEIS.forEach(campo => delete copia[campo])
    return JSON.stringify(copia)
}

function aplicaRedirectProtocolo(dominio){
    if(dominio.utiliza_https == 1 && window.location.protocol == 'http:'){
        loading.show()
        location.href = location.href.replace('http', 'https')
    }
    if(dominio.utiliza_https == 0 && window.location.protocol == 'https:'){
        loading.show()
        location.href = location.href.replace('https', 'http')
    }
}

export default {
    namespaced: true,
    state: {
        dominio: null,
    },
    getters: {
        pegaDominio(state){
            return state.dominio
        },
    },
    mutations: {
        atualizaDominio(state, dominio){
            state.dominio = dominio
        },
    },
    actions: {
        // Roda no load (App.vue) e, com o domínio já na tela, a cada navegação
        // (router). Com cache válido não vai à rede. Quando o domínio que chega
        // é diferente do que está na tela — cache venceu, ou outra aba já
        // trouxe o novo — devolve recarregar: o chat e o tema são aplicados uma
        // vez por load, então só recarregando a aba aberta usa o domínio novo.
        async carregaDominio({ commit, state }){
            const naTela = state.dominio
            // Tela servida pelo servidor da loja (PDV offline): o domínio vem do próprio app, que o
            // guardou da última vez que houve internet. Sem isto a página não abre sem a nuvem.
            if(typeof window != 'undefined' && window.__PDV_SERVIDOR__){
                const local = await leDominioDoServidorDaLoja()
                if(local){
                    if(!naTela){
                        commit('atualizaDominio', local)
                    }
                    return { status: 'success', data: { Dominio: local } }
                }
            }
            let retorno
            const cacheDados = leCacheDominio()
            if(cacheDados && cacheDados.Dominio){
                retorno = { status: 'success', data: cacheDados }
            }else{
                retorno = await $api.cGet(Vue.prototype.$urlBase + '/dominios/dados', {}, esperaDoBoot())
                if(retorno.status != 'success'){
                    // Sem resposta HTTP (rede fora): o $api devolve o XMLHttpRequest, cujo `status` é
                    // numérico e vale 0 quando a requisição nem chegou ao servidor. Aí vale o cache
                    // vencido (PDV offline). Erro DE servidor (500, 502, 504) NÃO entra aqui.
                    const semRede = semRespostaHttp(retorno)
                    const vencido = semRede ? leCacheDominio(true) : null
                    if(vencido && vencido.Dominio){
                        if(!naTela){
                            commit('atualizaDominio', vencido.Dominio)
                        }
                        return { status: 'success', data: vencido }
                    }
                    // Revalidação de aba aberta que falhou: segue com o que já está na tela
                    if(!naTela){
                        Vue.prototype.$dialog.error(semRede ? 'Sem conexão com o servidor.' : (retorno.message || 'Oops! Ocorreu algum erro no sistema.'))
                    }
                    return retorno
                }
                gravaCacheDominio(retorno.data)
            }
            const dominio = retorno.data.Dominio
            if(naTela && assinaturaDominio(naTela) != assinaturaDominio(dominio)){
                return Object.assign({}, retorno, { recarregar: true })
            }
            aplicaRedirectProtocolo(dominio)
            if(!naTela){
                commit('atualizaDominio', dominio)
            }
            return retorno
        }
    },
}
